import LoginPage, { PhoneType } from 'components/login/LoginPage'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { doGetChanges, joinGroupIfNeeded } from './GetChangesProvider'
import GuestMode from 'components/guest/GuestMode'
import { isTestEnv } from 'utils/env'
import { getOrUpdateDeviceId } from 'utils/device'
import Analytics, { Events } from 'analytics/Analytics'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { t } from 'i18next'
import { APP_COLOR } from 'utils/colors'
import Logger from 'services/Logger'

export enum CodeProvider {
  Firebase = 'firebase',
  Checkmobi = 'checkmobi'
}

interface AuthProviderProps {
  children: ReactNode
}

export interface AuthContextProps {
  onLogout: () => Promise<void> | undefined
  isAuth: boolean
  firstGetChangesFinished: boolean
  phoneType: PhoneType
  onPhoneType: (phoneType: PhoneType) => void
  codeProvider: CodeProvider
  onCodeProvider: (codeProvider: CodeProvider) => void
  isPreview: boolean
  syncAppWithServer: () => void
}

const AuthContext = createContext<AuthContextProps>({
  isAuth: false,
  firstGetChangesFinished: false,
  onLogout: function (): Promise<void> | undefined {
    return undefined
  },
  phoneType: PhoneType.Mobile,
  onPhoneType: () => {},
  codeProvider: CodeProvider.Firebase,
  onCodeProvider: () => {},
  isPreview: false,
  syncAppWithServer: () => {}
})

let defaultCodeProvider = CodeProvider.Checkmobi

export default function AuthProvider(props: AuthProviderProps): JSX.Element {
  const [phoneType, onPhoneType] = useState<PhoneType>(PhoneType.Mobile)
  const [firstGetChangesFinished, onFirstGetChangesFinished] = useState(false)
  const [isAuth, onAuth] = useState(
    !!LocalStorage.get(LocalStorageKey.ACCESS_TOKEN)
  )

  if (
    LocalStorage.get(LocalStorageKey.SAW_MAIN_SCREEN) &&
    !LocalStorage.get(LocalStorageKey.USER_FINSHED_ONBOARDING)
  ) {
    LocalStorage.set(LocalStorageKey.USER_FINSHED_ONBOARDING, '1')
  }

  const params = useParams()
  const [searchParams, onSearchParams] = useSearchParams()
  const [codeProvider, onCodeProvider] =
    useState<CodeProvider>(defaultCodeProvider)

  useEffect(() => {
    LocalStorage.set(LocalStorageKey.CODE_PROVIDER, defaultCodeProvider)
  }, [])

  const compositeGroupIdAndPass = params.groupIdAndPass

  Logger.pink('Link:', compositeGroupIdAndPass)

  let parts = compositeGroupIdAndPass?.split('⁠') ?? []

  Logger.pink('Link:Parts:', parts)

  let groupId = parts[0]
  let password = parts[1]

  let mobileLink = ''

  if (parts.length > 2) {
    mobileLink = parts[2]
    LocalStorage.set(LocalStorageKey.MOBILE_LINK, mobileLink)
  }

  Analytics.getInstance({
    amplitudeDeviceId: searchParams.get('aid'),
    singularDeviceId: searchParams.get('sid'),
    clevertapDeviceId: searchParams.get('cid')
  })
  if (groupId) {
    /**
     * This happens only when user pressed on a dynamic link to join/lookup a group
     * we need to cache this id in case of user not logged in
     * or launch join group in case of user logged in
     */

    LocalStorage.set(LocalStorageKey.CACHED_GROUP_ID_JOIN, groupId)
    LocalStorage.set(LocalStorageKey.CACHED_GROUP_PASSWORD, password)

    // if user already logged in, we need to try join group
    if (LocalStorage.get(LocalStorageKey.USER_ID)) {
      joinGroupIfNeeded()
    }
  }

  function setCodeProvider(provider: CodeProvider) {
    if (LocalStorage.get(LocalStorageKey.PROVIDER_OVERRIDE) === 'firebase') {
      onCodeProvider(CodeProvider.Firebase)
      LocalStorage.set(LocalStorageKey.CODE_PROVIDER, CodeProvider.Firebase)
      return
    }

    onCodeProvider(provider)
    LocalStorage.set(LocalStorageKey.CODE_PROVIDER, provider)
  }

  function syncAppWithServer() {
    doGetChanges(
      LocalStorage.get(LocalStorageKey.CLEAN_GET_CHANGES_DONE)
        ? LocalStorage.get(LocalStorageKey.DEVICE_ID)
        : null,
      undefined,
      LocalStorage.get(LocalStorageKey.LAST_UPDATE)
    ).then(() => {
      LocalStorage.set(LocalStorageKey.CLEAN_GET_CHANGES_DONE, true)
      onFirstGetChangesFinished(true)

      if (!LocalStorage.get(LocalStorageKey.SAW_MAIN_SCREEN) && isAuth) {
        LocalStorage.set(LocalStorageKey.SAW_MAIN_SCREEN, 1)
        Analytics.getInstance().sendEvent(Events.FIRST_MAIN_SCREEN)
      }
    })
  }

  useEffect(() => {
    if (isAuth) {
      syncAppWithServer()
    }
  }, [isAuth])

  const onLogout = async () => {
    const logout = await processLogout()
    onPhoneType(PhoneType.Mobile)
    onCodeProvider(defaultCodeProvider)
    onAuth(false)
    onFirstGetChangesFinished(false)

    return logout
  }

  window.processLogout = onLogout

  const [showLandlineDesc, setShowLandlineDesc] = useState(false)

  // checking embed group ids in url
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const groupIds = queryParams.get('groupIds')?.split(',') || []
  const calendarTitle = queryParams.get('calendarTitle') ?? ''
  const isPreview = queryParams.get('preview') === '1'

  useEffect(() => {
    setShowLandlineDesc(phoneType === PhoneType.Landline)
  }, [phoneType])

  if (isPreview || (groupId && groupId.length > 0) || groupIds.length > 0) {
    return (
      <GuestMode
        groupId={groupId}
        groupsIds={groupIds}
        calendarTitle={calendarTitle}
        preview
      />
    )
  }

  const children = !isAuth ? (
    <LoginPage isAuth={isAuth} onAuth={onAuth} />
  ) : (
    (props.children as JSX.Element)
  )

  return (
    <AuthContext.Provider
      value={{
        onLogout,
        isAuth,
        firstGetChangesFinished,
        phoneType,
        onPhoneType,
        codeProvider,
        onCodeProvider: setCodeProvider,
        isPreview,
        syncAppWithServer
      }}
    >
      <>
        {children}
        <ConfirmationDialog
          handleClose={() => setShowLandlineDesc(false)}
          handleLeftButton={() => {
            onPhoneType(PhoneType.Mobile)
            setShowLandlineDesc(false)
          }}
          handleRightButton={() => setShowLandlineDesc(false)}
          title={t('signingWithLandline')}
          content={t('signingWithLandlineDesc')}
          open={showLandlineDesc}
          rightButton={`${t('continue')}`}
          leftButtonColor="black"
          rightButtonColor={APP_COLOR}
        />
      </>
    </AuthContext.Provider>
  )
}

export async function processLogout() {
  const tfmPass = LocalStorage.get(LocalStorageKey.PASSWORD)
  const deviceuuid = getOrUpdateDeviceId()
  const expiryState = LocalStorage.get(LocalStorageKey.EXPIRY_DIALOG_SHOWN)
  const lang = LocalStorage.get('i18nextLng')
  const host = LocalStorage.get(LocalStorageKey.APP_HOST)
  LocalStorage.remove(LocalStorageKey.USER_ID)
  LocalStorage.clear()
  LocalStorage.set('i18nextLng', lang)
  LocalStorage.set(LocalStorageKey.DEVICE_ID, deviceuuid)
  LocalStorage.set(LocalStorageKey.EXPIRY_DIALOG_SHOWN, expiryState)
  if (host !== null && host !== undefined)
    LocalStorage.set(LocalStorageKey.APP_HOST, host)
  if (tfmPass && isTestEnv()) {
    LocalStorage.set(LocalStorageKey.PASSWORD, tfmPass)
    if (!LocalStorage.get(LocalStorageKey.SHOW_DEV_INFO)) {
      LocalStorage.set(LocalStorageKey.SHOW_DEV_INFO, 1)
    }
  }

  //clear db with a delay
  setTimeout(() => {
    Database.clear().then(() => {
      {
        console.log('Database cleared')
      }
    })
  }, 1000)

  Analytics.getInstance().handleLogout()
}

export function useAuth(): AuthContextProps {
  return useContext(AuthContext)
}
